import {
  fetchAuthSession,
  signIn as awsSignIn,
  signOut as awsSignOut,
  type AuthTokens,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { Observable, catchError, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { Pub, Service, Sub } from '../service.abstract.ts';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { storageHelper } from '../../helpers/storage/index.ts';
import { clearRecordedLoginUserIds } from '../userActivity/loginRecordSession.ts';
import { isCognitoThrottlingError } from '../../helpers/cognitoThrottling/cognitoThrottling.helper.ts';

export enum Auth {
  loggedIn = 'loggedIn',
  unassigned = 'unassigned',
  userNotFound = 'userNotFound',
  credentialsInvalid = 'credentialsInvalid',
  credentialsEmpty = 'credentialsEmpty',
}
export enum USER_ROLES {
  ADMIN = 'admin',
  NOT_DEFINED = 'no',
  CUSTOM_ACCESS = 'custom',
}
export enum APP_MODULES {
  CUSTOMER = '1',
  ARTICLE = '2',
  OFFER = '10',
  ORDER = '3',
  PRODUCTION = '4',
  INVOICE = '5',
  DEBITOR = '6',
  STATISTIC = '9',
  OPTIONS = '7',
  GLOBAL_SEARCH = '9999', // we don't have this module in the AWS congnito, but we need it always open
}
type Actions = 'signIn' | 'signOut';

let sessionExpirationInFlight: Promise<void> | null = null;

function isExpiredAuthSessionError(error: unknown): boolean {
  const { name = '', message = '' } = error as { name?: string; message?: string };

  if (name === 'UserUnAuthenticatedException' || name === 'UserUnauthenticatedException') {
    return true;
  }

  const normalizedMessage = `${name} ${message}`.toLowerCase();
  return [
    'refresh token has expired',
    'invalid refresh token',
    'session expired',
    'no current user',
    'not authenticated',
    'user needs to be authenticated',
  ].some((phrase) => normalizedMessage.includes(phrase));
}

function registerPostHogCompany(companyGuid: string): void {
  if (!posthog.__loaded) return;
  try {
    posthog.register({ company: companyGuid });
  } catch {
    // Analytics is best-effort; a PostHog fault must never break the auth flow.
  }
}

function resetPostHog(): void {
  if (!posthog.__loaded) return;
  try {
    posthog.reset();
  } catch {
    // Analytics is best-effort; a PostHog fault must never break sign-out.
  }
}

function expireSessionAndRedirectToLogin(): Promise<void> {
  if (sessionExpirationInFlight) return sessionExpirationInFlight;

  sessionExpirationInFlight = (async () => {
    storageHelper.session.removeItem('userAccessParams');
    storageHelper.session.removeItem('alternativeGuid');
    clearRecordedLoginUserIds();
    resetPostHog();

    try {
      await awsSignOut();
    } catch {
      // The session is already considered expired; redirect is more important
      // than surfacing a best-effort Cognito sign-out failure.
    }

    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  })().finally(() => {
    queueMicrotask(() => {
      sessionExpirationInFlight = null;
    });
  });

  return sessionExpirationInFlight;
}

/**
 * Failure ceiling for Cognito token reads (WA-3659, and the unfixed WA-3559).
 *
 * Amplify caches only SUCCESSFUL reads, so a session it cannot recover is
 * retried by every caller, indefinitely. Before WA-3659 one broken session
 * turned into tens of thousands of Cognito calls — `gqlClient` alone asked
 * twice per request — until the browser ran out of sockets and the app never
 * left its splash screen.
 *
 * Every token read in this app goes through `readAuthTokens`, so this ceiling
 * holds no matter which caller trips the failure first. Two kinds of failure
 * are deliberately NOT counted:
 *   - a throttled request (`TooManyRequestsException` / `ThrottlingException`,
 *     Sentry WAWIASSIST-255 / WA-3947): the session is intact and the next
 *     attempt may succeed. Amplify keeps the tokens on a rate limit for the
 *     same reason (`TokenOrchestrator.isAuthenticationError`);
 *   - anything from the IDENTITY pool: since WA-3947 only `userIdentityId()`
 *     talks to it, and an identity-pool failure was never meant to end a
 *     session (see `appAuthInit.ts`).
 */
const SESSION_FAILURE_LIMIT = 3;
const SESSION_FAILURE_WINDOW_MS = 10_000;

let recentSessionFailures: number[] = [];
let sessionReadsBlocked = false;

function resetSessionFailures(): void {
  recentSessionFailures = [];
  sessionReadsBlocked = false;
}

/**
 * On the login screen there is no session yet, so read failures are expected
 * and must not trip the breaker: `expireSessionAndRedirectToLogin` deliberately
 * skips the redirect when already on `/login`, so nothing would reload the page
 * and clear the tripped state — the sign-in that follows would stay blocked.
 */
function isOnLoginScreen(): boolean {
  return window.location.pathname === '/login';
}

function recordSessionFailure(error: unknown): void {
  // Calls already in flight when the breaker tripped land here afterwards.
  // Without this guard they keep growing the window and re-entering the expiry
  // path — whose single-flight slot is released on a microtask, so a late
  // arrival would start a second signOut + redirect.
  if (sessionReadsBlocked) return;
  if (isOnLoginScreen()) return;
  // A rate limit says nothing about the session (see the ceiling's doc above).
  if (isCognitoThrottlingError(error)) return;

  const now = Date.now();
  recentSessionFailures = recentSessionFailures.filter(
    (at) => now - at < SESSION_FAILURE_WINDOW_MS,
  );
  recentSessionFailures.push(now);

  if (recentSessionFailures.length < SESSION_FAILURE_LIMIT) return;

  // Block reads BEFORE ending the session: `expireSessionAndRedirectToLogin`
  // awaits a Cognito sign-out, and callers keep arriving during that await.
  sessionReadsBlocked = true;
  void expireSessionAndRedirectToLogin();
}

/**
 * In-flight token reads, one slot per kind (WA-3947).
 *
 * Amplify's `TokenOrchestrator.getTokens()` has no in-flight guard of its own
 * (only the refresh network call behind it is deduplicated), so N concurrent
 * callers on an expired token would each walk the refresh path — and each
 * would report its own failure to the breaker above for what is ONE Cognito
 * attempt. Collapsing them here means one `getTokens` call, one breaker
 * verdict, and one result handed to every caller.
 *
 * A plain read never joins a forced one: the forced read is a network
 * round-trip that can fail (throttled, offline) where the plain read would have
 * answered from the token store without touching the network at all.
 */
type TokenReadKind = 'plain' | 'forced';

const tokenReadsInFlight: Record<TokenReadKind, Promise<AuthTokens | null> | null> = {
  plain: null,
  forced: null,
};

function readAuthTokens(kind: TokenReadKind): Promise<AuthTokens | null> {
  const inFlight = tokenReadsInFlight[kind];
  if (inFlight) return inFlight;

  const read = (async () => {
    try {
      const tokens = await cognitoUserPoolsTokenProvider.getTokens({
        forceRefresh: kind === 'forced',
      });
      if (recentSessionFailures.length > 0 || sessionReadsBlocked) resetSessionFailures();
      return tokens;
    } catch (error) {
      recordSessionFailure(error);
      throw error;
    }
  })().finally(() => {
    // Clear on a microtask so callers that start a new read right after this
    // one settles land on a fresh slot — the same pattern as `refreshIdTokenOnce`.
    queueMicrotask(() => {
      tokenReadsInFlight[kind] = null;
    });
  });
  tokenReadsInFlight[kind] = read;
  return read;
}

/**
 * Single entry point for reading the Cognito USER-POOL tokens.
 *
 * Reads through `cognitoUserPoolsTokenProvider.getTokens` rather than
 * `fetchAuthSession` (WA-3947 / Sentry WAWIASSIST-255): the latter also
 * resolves AWS credentials whenever an `identityPoolId` is configured, i.e. one
 * `cognito-identity` round-trip per session read after every token refresh —
 * against an account-wide quota shared with other products, which is what got
 * throttled. GraphQL only ever needs the ID token; the identity pool is reached
 * solely by `userIdentityId()` for S3 uploads.
 *
 * Behaves like `getTokens` until the ceiling above is reached; from then on it
 * answers with NO tokens, immediately and without a network call, so an
 * unrecoverable session costs one bounded round of requests instead of a
 * storm. A successful read clears the counter.
 *
 * Empty rather than thrown on purpose. "No session" is a state every caller
 * here already handles, and it routes the failure into the quiet path the app
 * already has: `idToken`/`refreshIdToken` resolve to `''`, so `gqlClient` fails
 * with its own `Session expired` error, which it marks as expected before
 * redirecting (WA-3514…WA-3538 — an unmarked auth error reaches Sentry, and
 * Sentry opens Jira tickets for it). A bespoke error would instead need every
 * one of those paths to learn to recognise it.
 */
function resolveAuthTokens(options?: { forceRefresh?: boolean }): Promise<AuthTokens | null> {
  if (sessionReadsBlocked) return Promise.resolve(null);
  return readAuthTokens(options?.forceRefresh ? 'forced' : 'plain');
}

const idTokenOf = (tokens: AuthTokens | null): string => tokens?.idToken?.toString() ?? '';

/** A console-side company override in sessionStorage wins over the token's own claim. */
const companyGuidOf = (tokens: AuthTokens | null): string => {
  const guid = storageHelper.session.getItem('alternativeGuid');
  if (guid) return guid;
  return (tokens?.idToken?.payload?.['custom:company'] as string) ?? '';
};

/**
 * The module access rights, read from the ID token's own claims (WA-3950).
 *
 * `hasAccess()` used to force-refresh the session and then call
 * `fetchUserAttributes()`, which reads the session a second time — and
 * `fetchAuthSession` internally, i.e. the IDENTITY pool (`appAuthInit.ts`), on
 * every page load. That is the last non-S3 caller left on it after WA-3947, and
 * an identity-pool failure there is not tolerated by the credentials provider:
 * `NotAuthorizedException` (Sentry WAWIASSIST-256 — Cognito Identity refusing a
 * freshly issued ID token with "Couldn't verify signed token") propagates, and
 * this pipe's `catchError` hands the user `initUserAccessParams` — every module
 * open — for that page load.
 *
 * These are the same three claims, from the same token: the server reads
 * `custom:access_rights` / `custom:access_custom_rights` / `custom:role` out of
 * the Authorization token to enforce its `@access` directive, and the token
 * here is the one the next request will carry. So the UI and the server now
 * decide from one source instead of two that can disagree, with no second
 * session read, no `GetUser` round trip and no identity pool.
 *
 * Claims that are absent or malformed keep the behaviour attributes had: fall
 * back to the defaults, but honour `custom:role` for the admin bar.
 */
const accessParamsOf = (tokens: AuthTokens | null): IUserAccessParams => {
  const claims = tokens?.idToken?.payload;
  const userAccessParams = structuredClone(initUserAccessParams);
  if (!claims) return userAccessParams;

  const isAdminMode = claims['custom:role'] === 'admin';
  try {
    const { faw: initRole } = JSON.parse(claims['custom:access_rights'] as string);
    const { faw: modulesAccessRights = [] } = JSON.parse(
      claims['custom:access_custom_rights'] as string,
    ) as { faw: string[] };

    const roleMap: Record<string, USER_ROLES> = {
      [USER_ROLES.NOT_DEFINED]: USER_ROLES.NOT_DEFINED,
      [USER_ROLES.CUSTOM_ACCESS]: USER_ROLES.CUSTOM_ACCESS,
      [USER_ROLES.ADMIN]: USER_ROLES.ADMIN,
    };
    const role = roleMap[initRole] ?? USER_ROLES.NOT_DEFINED;

    // for USER_ROLES.NOT_DEFINED we open all modules too. Because it's a default role for new users.
    if (role === USER_ROLES.NOT_DEFINED || role === USER_ROLES.ADMIN) {
      return { ...initUserAccessParams, role, isAdminMode };
    }

    // when role === USER_ROLES.CUSTOM_ACCESS
    modulesAccessRights.forEach((moduleAccessRight) => {
      const [moduleId, isOpen] = moduleAccessRight.split(',');
      if (moduleId in userAccessParams.modules) {
        userAccessParams.modules[moduleId as APP_MODULES] = isOpen === '1';
      }
    });
    return { ...userAccessParams, role: USER_ROLES.CUSTOM_ACCESS, isAdminMode };
  } catch {
    return { ...initUserAccessParams, isAdminMode };
  }
};

class PubImpl extends Pub<Actions> {
  signIn(credentials: ICredentials): void {
    this.emit('signIn', credentials);
  }
  signOut(): void {
    this.emit('signOut');
  }
}

class SubImpl extends Sub<Actions> {
  idToken(): Observable<string> {
    return from(resolveAuthTokens()).pipe(map(idTokenOf));
  }
  /**
   * Force-refresh the Cognito session and return the fresh idToken.
   *
   * Used by the GraphQL client to recover from 401s caused by expired
   * tokens — see WA-3514 / WA-3515. `getTokens({ forceRefresh: true })`
   * exchanges the refreshToken for a new idToken bypassing Amplify's local
   * cache; the result is `''` only if the refresh token itself is dead, in
   * which case the caller should bounce the user to the login flow.
   */
  refreshIdToken(): Observable<string> {
    return from(resolveAuthTokens({ forceRefresh: true })).pipe(
      map(idTokenOf),
      catchError((error: unknown) => {
        if (isExpiredAuthSessionError(error)) {
          return of('');
        }
        return throwError(() => error);
      }),
    );
  }
  signIn(): Observable<IAuth> {
    return this.actionListener('signIn').pipe(
      switchMap(({ params }) => {
        const { username, password } = params as ICredentials;
        // A deliberate sign-in starts a new session: clear any breaker state
        // left by the dead one, or the app would stay blocked after logging in.
        resetSessionFailures();
        return from(awsSignIn({ username, password })).pipe(
          map(() => ({ status: Auth.loggedIn })),
          catchError((error: Error) => {
            switch (error.name) {
              case 'UserNotFoundException':
                return of({ status: Auth.userNotFound });
              case 'NotAuthorizedException':
                return of({ status: Auth.credentialsInvalid });
              case 'InvalidParameterException':
                return of({ status: Auth.credentialsEmpty });
              default:
                return of({ status: Auth.unassigned });
            }
          }),
        );
      }),
    );
  }
  signOut(): Observable<any> {
    return this.actionListener('signOut').pipe(
      switchMap(() => {
        storageHelper.session.removeItem('userAccessParams');
        clearRecordedLoginUserIds();
        resetPostHog();
        return from(awsSignOut());
      }),
    );
  }
  isLoggedIn(): Observable<IAuth> {
    return from(resolveAuthTokens()).pipe(
      map((tokens) => {
        if (!tokens?.idToken) {
          return { status: Auth.unassigned };
        }
        const payload = tokens.idToken.payload;
        const username = payload['cognito:username'] as string;
        const companyGuid = payload['custom:company'] as string | undefined;
        const userEmail = payload['email'] as string | undefined;

        Sentry.setUser({ email: userEmail, username });
        Sentry.setTag('company', companyGuid);

        if (companyGuid) {
          registerPostHogCompany(companyGuid);
        }

        return { status: Auth.loggedIn };
      }),
      catchError(() => of({ status: Auth.unassigned })),
    );
  }
  /**
   * Synchronous read of the cached access params (or null when absent).
   *
   * Lets a component seed its initial render from the already-warmed cache —
   * `ProtectedRoute` resolves `getCachedUserAccessParams()` before the layout
   * mounts, so by then this is populated. Seeding avoids a first-paint with
   * default params followed by a post-`hasAccess()` correction, which shifts
   * the admin bar / sidebar and flickers the top bar + left menu (WA-3494).
   */
  getCachedUserAccessParamsSync(): IUserAccessParams | null {
    const cachedData = storageHelper.session.getItem('userAccessParams');
    return cachedData ? (cachedData as IUserAccessParams) : null;
  }
  getCachedUserAccessParams(): Observable<IUserAccessParams> {
    const cachedData = this.getCachedUserAccessParamsSync();
    if (cachedData) {
      return of(cachedData);
    }
    return this.hasAccess();
  }
  hasAccess(): Observable<IUserAccessParams> {
    // Force-refreshed on purpose: the claims below are the user's CURRENT
    // access rights only if the token was just reissued, which is also why the
    // app no longer needs a second read to see them (`accessParamsOf`).
    return from(resolveAuthTokens({ forceRefresh: true })).pipe(
      map(accessParamsOf),
      tap((userAccessParams) => {
        storageHelper.session.setItem('userAccessParams', userAccessParams);
      }),
      catchError(() => of(initUserAccessParams)),
    );
  }
  customerGUID(): Observable<string> {
    return from(resolveAuthTokens()).pipe(map(companyGuidOf));
  }
  /**
   * Everything an authenticated request needs, from ONE token read (WA-3947).
   *
   * `gqlClient` used to `zip(idToken(), customerGUID())` — two session reads
   * per request, i.e. two concurrent refreshes the moment a token expires
   * (and, before WA-3947, two identity-pool calls). The in-flight slot in
   * `readAuthTokens` already collapses such pairs; this keeps the client from
   * starting two in the first place.
   */
  requestIdentity(): Observable<IRequestIdentity> {
    return from(resolveAuthTokens()).pipe(
      map((tokens) => ({ idToken: idTokenOf(tokens), companyGuid: companyGuidOf(tokens) })),
    );
  }
  /**
   * The Cognito IDENTITY-pool id the private S3 prefix is keyed on
   * (`private/{identityId}/…`, see `files.helper.ts`) — the ONLY session read
   * in this app that still goes through `fetchAuthSession`, because it is the
   * only one that needs the identity pool (WA-3947).
   *
   * Outside the breaker on purpose: an identity-pool failure says nothing
   * about the user-pool session, so it must never count towards ending it.
   * The network-tolerant provider in `appAuthInit.ts` already turns the
   * transient ones (offline, throttled) into "no identityId", which
   * `files.helper.ts` reports as a connection drop; and `files.helper.ts`
   * reads the ID token through the breaker first, so a dead session is
   * caught before this is ever reached.
   */
  userIdentityId(): Observable<string> {
    if (sessionReadsBlocked) return of('');
    return from(fetchAuthSession()).pipe(map((session) => session.identityId ?? ''));
  }
}
class AuthService extends Service<Actions> {
  public pub = new PubImpl(this.stream$);
  public sub = new SubImpl(this.stream$);

  expireSession(): Promise<void> {
    return expireSessionAndRedirectToLogin();
  }
}
export const authService = new AuthService();

export const initUserAccessParams: IUserAccessParams = {
  role: USER_ROLES.NOT_DEFINED,
  modules: {
    [APP_MODULES.CUSTOMER]: true,
    [APP_MODULES.ARTICLE]: true,
    [APP_MODULES.OFFER]: true,
    [APP_MODULES.ORDER]: true,
    [APP_MODULES.PRODUCTION]: true,
    [APP_MODULES.INVOICE]: true,
    [APP_MODULES.DEBITOR]: true,
    [APP_MODULES.STATISTIC]: true,
    [APP_MODULES.OPTIONS]: true,
    [APP_MODULES.GLOBAL_SEARCH]: true,
  },
  isAdminMode: false,
};

export interface ICredentials {
  username: string;
  password: string;
  alternativeguid?: string;
}

export interface IUserAccessParams {
  role: USER_ROLES;
  modules: Record<APP_MODULES, boolean>;
  isAdminMode: boolean;
}

/** What `gqlClient` puts on the wire for one request — see `requestIdentity()`. */
export interface IRequestIdentity {
  idToken: string;
  companyGuid: string;
}

interface IAuth {
  status: Auth;
}
